import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { getMicroLearningHistory } from '../../../api/api'
import { MicroLearningHistoryEntry } from '../../../api/models'
import { AppStateContext } from '../../../state/AppProvider'

const DEFAULT_PAGE_SIZE = 20

type UseMicroLearningHistoryResult = {
  logs: MicroLearningHistoryEntry[]
  isLoading: boolean
  hasMore: boolean
  loadMore: () => void
}

export const useMicroLearningHistory = (
  mlId: string | undefined,
  refreshSignal = 0,
): UseMicroLearningHistoryResult => {
  const appStateContext = useContext(AppStateContext)
  const pageSize = appStateContext?.state.frontendSettings?.micro_learning?.history_page_size ?? DEFAULT_PAGE_SIZE

  const [logs, setLogs] = useState<MicroLearningHistoryEntry[]>([])
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const isFetching = useRef(false)
  const prevRefreshSignal = useRef(refreshSignal)

  useEffect(() => {
    setLogs([])
    setPage(1)
    setHasMore(true)
  }, [mlId])

  useEffect(() => {
    if (prevRefreshSignal.current === refreshSignal) return
    prevRefreshSignal.current = refreshSignal

    isFetching.current = false
    setPage(1)
    setHasMore(true)
    setLogs([])
  }, [refreshSignal])

  useEffect(() => {
    if (!mlId || isFetching.current) return

    let cancelled = false
    isFetching.current = true
    setIsLoading(true)

    getMicroLearningHistory(mlId, page, pageSize).then(result => {
      if (cancelled) return
      isFetching.current = false
      setIsLoading(false)

      if (result.length < pageSize) {
        setHasMore(false)
      }

      setLogs(prev => (page === 1 ? result : [...prev, ...result]))
    })

    return () => {
      cancelled = true
      isFetching.current = false
    }
  }, [mlId, page, pageSize, refreshSignal])

  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      setPage(p => p + 1)
    }
  }, [isLoading, hasMore])

  return { logs, isLoading, hasMore, loadMore }
}
